import React, {
  useCallback, useImperativeHandle, useRef, useState,
} from "react"
import types from "prop-types"
import clsx from "clsx"
import { CircularIconButton } from "shared/buttons"

const ItemRow = ({
  collapsable = false,
  className = "",
  itemRowBody = null,
  itemRowBodyClassName = "",
  itemRowHeaderContent,
  itemRowHeaderContentClassName = "",
  itemRowHeaderControls = null,
  itemRowHeaderControlsClassName = "",
  onCollapse = undefined,
  onExpand = undefined,
  ref = undefined,
}) => {
  const containerRef = useRef()

  const [collapsed, setCollapsed] = useState(true)

  const expand = useCallback(() => {
    if (collapsed) {
      setCollapsed(false)
      onExpand?.()
    }
  }, [collapsed, onExpand])

  const collapse = useCallback(() => {
    if (collapsable && !collapsed) {
      setCollapsed(true)
      onCollapse?.()
    }
  }, [collapsable, collapsed, onCollapse])

  const toggleCollapsed = collapsed ? expand : collapse
  const icon = collapsed ? "chevron-down" : "chevron-up"
  const ariaLabel = collapsed ? "expand row" : "collapse row"

  // If a ref is passed to ItemRow, then the ref's "current" value
  // will be the object returned by the function passed as the second argument
  // to useImperativeHandle().
  // https://react.dev/reference/react/useImperativeHandle
  useImperativeHandle(ref, () => (
    {
      collapse() {
        collapse()
      },
      collapsed,
      expand() {
        expand()
      },
      scrollIntoView() {
        containerRef.current.scrollIntoView()
      },
    }
  ), [collapse, collapsed, expand])

  return (
    <div
      className={clsx("item-row", className)}
      ref={containerRef}
    >
      <div className="item-row-header">
        <div className={clsx("item-row-header-content", itemRowHeaderContentClassName)}>
          {itemRowHeaderContent}
        </div>
        {
          (itemRowHeaderControls || collapsable) && (
            <div className={clsx("item-row-header-controls", itemRowHeaderControlsClassName)}>
              {
                collapsable && itemRowBody && (
                  <CircularIconButton icon={icon} onClick={toggleCollapsed} ariaLabel={ariaLabel} />
                )
              }
              {itemRowHeaderControls}
            </div>
          )
        }
      </div>
      {
        itemRowBody && (
          <div className={clsx("item-row-body", itemRowBodyClassName, collapsable && collapsed && "hidden")}>
            {itemRowBody}
          </div>
        )
      }
    </div>
  )
}

ItemRow.displayName = "ItemRow"

ItemRow.propTypes = {
  collapsable: types.bool,
  className: types.string,
  itemRowBody: types.node,
  itemRowBodyClassName: types.string,
  itemRowHeaderContent: types.node.isRequired,
  itemRowHeaderContentClassName: types.string,
  itemRowHeaderControls: types.node,
  itemRowHeaderControlsClassName: types.string,
  onCollapse: types.func,
  onExpand: types.func,
  ref: types.oneOfType([types.func, types.object]),
}

export default ItemRow
